import {
  EMPLOYMENT_FORMS,
  type EmploymentForm,
  isEmploymentForm,
  type RequisiteIssue,
  validateBik,
  validateInn,
  validateOgrn,
  validateSettlementAccount,
} from '@glory/kernel';

import type { IsoDate } from './rates.ts';

/**
 * Форма работы сотрудника (раздел 4 спецификации подпроекта 4): самозанятый (НПД), ИП, физлицо по договору ГПХ,
 * штат. От неё зависят договор, который сотрудник подпишет, и личные данные, которые для него нужны. Список — в
 * kernel: его знают приглашение (identity), договоры (documents) и контракты API.
 */
export { EMPLOYMENT_FORMS, type EmploymentForm, isEmploymentForm };

/**
 * Личные данные сотрудника для договора. Номера хранятся цифрами (паспорт — 10 цифр серии и номера, код
 * подразделения — 6, СНИЛС — 11), показываются с разделителями. Паспорт, СНИЛС и счёт в базе зашифрованы.
 */
export interface EmploymentData {
  /** Полное ФИО, как в паспорте. */
  readonly fullName: string | null;
  readonly birthDate: IsoDate | null;
  /** Серия и номер: 10 цифр. */
  readonly passportNumber: string | null;
  readonly passportIssuedBy: string | null;
  readonly passportIssuedOn: IsoDate | null;
  /** Код подразделения: 6 цифр. */
  readonly passportDivisionCode: string | null;
  /** Адрес регистрации. */
  readonly address: string | null;
  /** ИНН физлица: 12 цифр. */
  readonly inn: string | null;
  /** СНИЛС: 11 цифр. */
  readonly snils: string | null;
  /** ОГРНИП: 15 цифр — у ИП. */
  readonly ogrnip: string | null;
  readonly bankBik: string | null;
  readonly bankName: string | null;
  /** Счёт для выплат: 20 цифр, сверяется с БИК по ключу. */
  readonly bankAccount: string | null;
  /** Дата постановки на учёт самозанятым (справка из «Мой налог»). */
  readonly npdConfirmedOn: IsoDate | null;
}

export type EmploymentField = keyof EmploymentData;

export const EMPTY_EMPLOYMENT: EmploymentData = {
  fullName: null,
  birthDate: null,
  passportNumber: null,
  passportIssuedBy: null,
  passportIssuedOn: null,
  passportDivisionCode: null,
  address: null,
  inn: null,
  snils: null,
  ogrnip: null,
  bankBik: null,
  bankName: null,
  bankAccount: null,
  npdConfirmedOn: null,
};

/** Поля паспорта: в базе — одной зашифрованной записью. */
export const PASSPORT_FIELDS = [
  'passportNumber',
  'passportIssuedBy',
  'passportIssuedOn',
  'passportDivisionCode',
] as const satisfies readonly EmploymentField[];

/** Что шифруется в базе (раздел 4): паспорт, СНИЛС и счёт. */
export const SEALED_FIELDS = [
  ...PASSPORT_FIELDS,
  'snils',
  'bankAccount',
] as const satisfies readonly EmploymentField[];

const COMMON: readonly EmploymentField[] = [
  'fullName',
  'birthDate',
  'passportNumber',
  'passportIssuedBy',
  'passportIssuedOn',
  'passportDivisionCode',
  'address',
  'inn',
];
const BANK: readonly EmploymentField[] = ['bankBik', 'bankName', 'bankAccount'];

/** Какие поля есть у формы — в порядке экрана «Данные и договор». */
const FIELDS: Readonly<Record<EmploymentForm, readonly EmploymentField[]>> = {
  npd: [...COMMON, 'npdConfirmedOn', ...BANK],
  ip: [...COMMON, 'ogrnip', ...BANK],
  gph: [...COMMON, 'snils', ...BANK],
  staff: [...COMMON, 'snils', ...BANK],
};

/**
 * Что обязательно для договора. ИНН обязателен самозанятому и ИП (они платят налог сами), у физлица по ГПХ и в штате —
 * если есть; СНИЛС — у ГПХ и штата (взносы платит студия), ОГРНИП — у ИП, дата постановки на учёт — у самозанятого.
 */
const OPTIONAL: Readonly<Record<EmploymentForm, readonly EmploymentField[]>> = {
  npd: [],
  ip: [],
  gph: ['inn'],
  staff: ['inn'],
};

export const employmentFieldsOf = (form: EmploymentForm): readonly EmploymentField[] => FIELDS[form];

export const requiredEmploymentFields = (form: EmploymentForm): readonly EmploymentField[] =>
  FIELDS[form].filter((field) => !OPTIONAL[form].includes(field));

export const TEXT_MAX = 500;

/** Проблема в поле: формат, контрольная сумма, дата. */
export interface EmploymentIssue {
  readonly field: EmploymentField;
  readonly issue:
    | RequisiteIssue
    | 'required'
    | 'too_long'
    | 'inn_not_person'
    | 'ogrn_not_individual'
    | 'snils_format'
    | 'snils_checksum'
    | 'passport_format'
    | 'division_code_format'
    | 'date_invalid'
    | 'date_future';
}

const text = (value: string | null | undefined): string | null => {
  const trimmed = value?.replace(/\s+/g, ' ').trim() ?? '';
  return trimmed === '' ? null : trimmed;
};
const digits = (value: string | null | undefined): string | null => text(value?.replace(/[\s\-–—]/g, ''));

const NUMBER_FIELDS: readonly EmploymentField[] = [
  'passportNumber',
  'passportDivisionCode',
  'inn',
  'snils',
  'ogrnip',
  'bankBik',
  'bankAccount',
];

/**
 * Как ввели → как храним: без лишних пробелов, номера — без пробелов и дефисов; поля, которых у формы нет (ОГРНИП
 * у штатного), пропадают.
 */
export function normalizeEmployment(form: EmploymentForm, input: Partial<EmploymentData>): EmploymentData {
  const own = FIELDS[form];
  const pick = (field: EmploymentField): string | null => {
    if (!own.includes(field)) return null;
    const value = input[field] ?? null;
    return NUMBER_FIELDS.includes(field) ? digits(value) : text(value);
  };
  return Object.fromEntries(
    (Object.keys(EMPTY_EMPLOYMENT) as EmploymentField[]).map((field) => [field, pick(field)]),
  ) as unknown as EmploymentData;
}

const SNILS_WEIGHTS = [9, 8, 7, 6, 5, 4, 3, 2, 1];
/** Номера до 001-001-998 выдавались без контрольного числа — их сверять не с чем. */
const SNILS_UNCHECKED_MAX = 1_001_998;

/** СНИЛС: 11 цифр, последние две — контрольное число по первым девяти. */
export function validateSnils(value: string): 'snils_format' | 'snils_checksum' | null {
  if (!/^\d{11}$/.test(value)) return 'snils_format';
  const body = value.slice(0, 9);
  if (Number(body) <= SNILS_UNCHECKED_MAX) return null;
  const sum = SNILS_WEIGHTS.reduce((acc, weight, i) => acc + weight * Number(body[i]), 0);
  const check = sum < 100 ? sum : sum <= 101 ? 0 : (sum % 101) % 100;
  return check === Number(value.slice(9)) ? null : 'snils_checksum';
}

/** ИНН физлица: 12 цифр с двумя контрольными. */
export function validatePersonInn(value: string): RequisiteIssue | 'inn_not_person' | null {
  const issue = validateInn(value);
  if (issue) return issue;
  return value.length === 12 ? null : 'inn_not_person';
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

const isRealDate = (value: string): boolean => {
  if (!ISO.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const DATE_FIELDS: readonly EmploymentField[] = ['birthDate', 'passportIssuedOn', 'npdConfirmedOn'];

/**
 * Что не так с заполненными полями (данные уже нормализованы). Пустые поля здесь не ошибка — данные сохраняются
 * по мере заполнения; полноту для договора проверяет `missingEmploymentFields`.
 */
export function employmentIssues(data: EmploymentData, today: IsoDate): EmploymentIssue[] {
  const issues: EmploymentIssue[] = [];
  const check = (
    field: EmploymentField,
    validate: (value: string) => EmploymentIssue['issue'] | null,
  ): void => {
    const value = data[field];
    if (value === null) return;
    const issue = validate(value);
    if (issue) issues.push({ field, issue });
  };
  for (const field of ['fullName', 'passportIssuedBy', 'address', 'bankName'] as const)
    check(field, (value) => (value.length > TEXT_MAX ? 'too_long' : null));
  for (const field of DATE_FIELDS)
    check(field, (value) => (!isRealDate(value) ? 'date_invalid' : value > today ? 'date_future' : null));
  check('passportNumber', (value) => (/^\d{10}$/.test(value) ? null : 'passport_format'));
  check('passportDivisionCode', (value) => (/^\d{6}$/.test(value) ? null : 'division_code_format'));
  check('inn', validatePersonInn);
  check('snils', validateSnils);
  check('ogrnip', (value) => {
    const issue = validateOgrn(value);
    if (issue) return issue;
    return value.length === 15 ? null : 'ogrn_not_individual';
  });
  check('bankBik', validateBik);
  const bik = data.bankBik;
  const bikValid = bik !== null && validateBik(bik) === null;
  check('bankAccount', (account) =>
    bikValid ? validateSettlementAccount(account, bik) : /^\d{20}$/.test(account) ? null : 'account_format',
  );
  return issues;
}

/** Каких обязательных полей формы не хватает для договора — в порядке экрана. */
export function missingEmploymentFields(form: EmploymentForm, data: EmploymentData): EmploymentField[] {
  return requiredEmploymentFields(form).filter((field) => data[field] === null);
}

/** Данные готовы для договора: всё обязательное заполнено и без ошибок. */
export const employmentComplete = (form: EmploymentForm, data: EmploymentData, today: IsoDate): boolean =>
  missingEmploymentFields(form, data).length === 0 && employmentIssues(data, today).length === 0;

/** «4510 123456». */
export const passportNumberText = (value: string): string =>
  /^\d{10}$/.test(value) ? `${value.slice(0, 4)} ${value.slice(4)}` : value;

/** «770-001». */
export const divisionCodeText = (value: string): string =>
  /^\d{6}$/.test(value) ? `${value.slice(0, 3)}-${value.slice(3)}` : value;

/** «123-456-789 01». */
export const snilsText = (value: string): string =>
  /^\d{11}$/.test(value)
    ? `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6, 9)} ${value.slice(9)}`
    : value;
